/**
 * ExtendedLabel is using digitNode prefab instants for each digit.
 * digitPrefab is a node with Label component, where the lineHeight is defined properly to work with bitmapFonts.
 * digitPrefab instant's origin point should be in the center of the node not to have problems with anchor position.
 *
 * ExtendedLabel usage examples:
 * this.label.setFontData(this.font1);
 * this.label.startProgress(2, 1234, [200, 500, 1000]).then(() => {
 *  code to handle animation end
 * };
 *
 * this.label.setFontData(this.font1);
 * this.label.setNumber(1234);
 *
 * this.label.node.on('checkpoint', ({ checkpoint }) => {
            switch (checkpoint) {
                case 1:
                    this.label.setFontData(this.font2);
                    break;
                case 2:
                    this.label.setFontData(this.font3);
                    break;
            }
 */

const State = cc.Enum({
    IDLE: 'idle',
    PROGRESS: 'progress',
    STAMP: 'stamp',
    WAVE: 'wave'
});

const i18n = require('i18n');

cc.Class({
    extends: cc.Component,

    properties: {
        lineHeight: 0
    },

    ctor() {
        this.digitNodes = [];
        this.promises = [];
        this.digitPool = new cc.NodePool();
        this.score = 0;
    },

    onLoad() {
        this.horizontalLayout = this.node.addComponent(cc.Layout);
        this.horizontalLayout.type = cc.Layout.Type.HORIZONTAL;
        this.horizontalLayout.resizeMode = cc.Layout.ResizeMode.CONTAINER;

        for (let i = 0; i < 15; i++) {
            const digitNode = new cc.Node('Digit');
            const digitLabel = digitNode.addComponent(cc.Label);

            digitLabel.lineHeight = this.lineHeight;
            digitLabel.fontSize = 100;
            this.digitPool.put(digitNode);

        }
    },

    /**
     * Set label data
     * @param font
     * @param color
     * @param size
     */
    setFontData(font, fontSize = 100, color = cc.Color.WHITE) {
        this.font = font;
        this.color = color;
        this.fontSize = fontSize;
        this.digitNodes.forEach((digitNode) => {
            digitNode.getComponent(cc.Label).font = this.font;
            digitNode.getComponent(cc.Label).fontSize = this.fontSize;
            digitNode.color = this.color;
        });
    },

    /**
     * Set a number
     * @param score
     */
    setNumber(score) {
        const scoreString = i18n.formatCurrencySmall(score);

        this.string = scoreString;
        for (let i = 0; i < scoreString.length; i++) {
            if (!this.digitNodes[i]) {
                this.digitNodes[i] = this._createDigit(scoreString[i]);
            } else {
                this.digitNodes[i].getComponent(cc.Label).string = scoreString[i];
            }
        }
    },
    /**
     * Start a wave animation beginning from the most left digit
     * @param digitDelay
     * @param digitAnimationDuration
     * @returns {Promise}
     */
    startWave(digitDelay, digitAnimationDuration, score) {
        return new Promise((resolve) => {
            this.state = State.WAVE;
            this.cleanup();
            this.setNumber(score);
            this.promises.length = 0;

            this.digitNodes.forEach((digitNode, index) => {
                this.promises.push(new Promise((resolve) => {
                    digitNode.runAction(cc.sequence( // main animation
                        cc.delayTime((index + 1) * digitDelay),    // wait for start delay
                        cc.scaleTo(digitAnimationDuration / 3, 1.4, 1.4),
                        cc.scaleTo(digitAnimationDuration / 3, 0.7, 0.7),
                        cc.scaleTo(digitAnimationDuration / 3, 1, 1),
                        cc.callFunc(() => {
                            resolve();
                        })
                    ));
                }));
            });
            Promise.all(this.promises).then(() => {
                this.state = State.IDLE;
                resolve();
            });
        });
    },

    /**
     *
     * Start counter animation of the score. Emit an event 'checkpoint' with the data of the checkpoint index,
     * after each threshold
     * @param progressTime
     * @param score
     * @param checkPoints
     * @returns {Promise}
     */
    startProgress(progressTime, score, checkPoints = []) {
        return new Promise((resolve) => {
            if (this.state === State.PROGRESS) {
                this._onProgressComplete();
            }
            this.cleanup();

            this.score = 0;
            this.targetScore = score;
            let checkpointIndex = 0;

            this.state = State.PROGRESS;
            this.progressResolve = resolve;
            this.progressTween = cc.tween(this).to(progressTime, { score }, {
                progress: (start, end, current, ratio) => {
                    const currentScore = (end - start) * ratio;

                    if (checkPoints[checkpointIndex] && currentScore > checkPoints[checkpointIndex]) {
                        this.node.emit('checkpoint', { checkpoint: checkpointIndex + 1 });
                        checkpointIndex++;
                    }

                    this.setNumber(currentScore);
                    // return currentScore;
                }
                // easing: 'sineIn'
            }).call(() => {
                this._onProgressComplete();
            }).start();
        });
    },

    _onProgressComplete() {
        if (this.progressResolve) {
            this.progressResolve();
            this.progressResolve = null;
        }
        this.setNumber(this.targetScore);
        this.state = State.IDLE;
        this.node.emit('progressFinish');

        if (this.progressTween) {
            this.progressTween.stop();
            this.progressTween = null;
        }
    },

    finishProgress() {
        if (this.state !== State.PROGRESS) {
            return;
        }
        this._onProgressComplete();
    },
    /**
     * * Start stamp animation for each digit. Emit an event 'stamp' after each effect ends.
     * @param digitDelay
     * @param digitAnimationDuration
     * @param score
     * @returns {Promise}
     */
    startStamp(digitDelay, digitAnimationDuration, score) {
        this.cleanup();
        this.setNumber(score);

        this.promises.length = 0;
        this.state = State.STAMP;
        this.digitNodes.forEach((digit, index) => {
            digit.opacity = 1;
            digit.stopAllActions();

            this.promises.push(new Promise((resolve) => {
                digit.runAction(cc.sequence(
                    cc.delayTime((index + 1) * digitDelay),
                    cc.callFunc(() => {
                        digit.setScale(2.5, 2.5);
                        digit.opacity = 255;
                        this.scheduleOnce(() => {
                            this.node.emit('stamp', { index });
                        }, digitAnimationDuration * 0.3);
                    }),
                    cc.scaleTo(digitAnimationDuration, 1, 1).easing(cc.easeElasticOut(0.5)),
                    cc.callFunc(() => {
                        resolve();
                    })
                ));
            }));
        });

        return Promise.all(this.promises);
    },

    cleanup() {
        this.digitNodes.forEach((digit) => {
            digit.cleanup();
        });
        this.node.cleanup();
        this.unscheduleAllCallbacks();
    },

    hide(duration) {
        return new Promise((resolve) => {
            this.cleanup();
            cc.tween(this.node).to(duration, { scale: 0 }).call(() => {
                this.node.setPosition(0, 0);
                    this.node.setScale(1, 1);

                    this.digitNodes.forEach((digit) => {
                        digit.parent = null;
                        digit.setScale(1, 1);
                        digit.opacity = 255;
                        this.digitPool.put(digit);
                    });
                    this.digitNodes.length = 0;
                    resolve();
            }).start();
        });
    },

    /**
     * Get pooled node from digitPool for the digit. By default 15 nodes should be instantiated in the NodePool.
     * @param number
     * @returns {cc.Node}
     * @private
     */
    _createDigit(number) {
        const digitNode = this.digitPool.get();
        const digitLabel = digitNode.getComponent(cc.Label);

        digitNode.color = this.color || cc.Color.WHITE;
        digitLabel.fontSize = this.fontSize || 100;
        digitLabel.font = this.font;
        digitLabel.string = number || '';

        this.node.addChild(digitNode);
        this.horizontalLayout.updateLayout();

        return digitNode;
    }
});
